import { SyncProgressImpl } from './SyncProgress.js';
import { FULL_SYNC_PRIORITY } from '../../constants.js';
export class SyncStatusSnapshot {
    core;
    jsState;
    constructor(core, jsState) {
        this.core = core;
        this.jsState = jsState;
    }
    get connected() {
        return this.core?.connected ?? false;
    }
    get connecting() {
        return this.core?.connecting ?? false;
    }
    get downloading() {
        return this.core?.downloading != null;
    }
    get uploading() {
        return this.jsState.uploading ?? false;
    }
    get downloadError() {
        return this.jsState.downloadError;
    }
    get uploadError() {
        return this.jsState.uploadError;
    }
    get dataFlowStatus() {
        return {
            downloading: this.downloading,
            uploading: this.uploading,
            downloadError: this.downloadError,
            uploadError: this.uploadError
        };
    }
    get lastSyncedAt() {
        return this.statusForPriority(FULL_SYNC_PRIORITY)?.lastSyncedAt;
    }
    get hasSynced() {
        return this.statusForPriority(FULL_SYNC_PRIORITY)?.hasSynced;
    }
    get syncStreams() {
        return this.core?.streams.map((core) => new SyncStreamStatusView(this, core));
    }
    forStream(stream) {
        const asJson = JSON.stringify(stream.parameters);
        const raw = this.core?.streams?.find((r) => r.name == stream.name && asJson == JSON.stringify(r.parameters));
        return raw && new SyncStreamStatusView(this, raw);
    }
    get priorityStatusEntries() {
        return this.core?.priority_status.map(priorityToJs);
    }
    get downloadProgress() {
        const internalProgress = this.core?.downloading;
        if (internalProgress == null) {
            return null;
        }
        return new SyncProgressImpl(internalProgress);
    }
    statusForPriority(priority) {
        const coreStatus = this.core?.priority_status;
        if (coreStatus == null)
            return undefined;
        // priorityStatusEntries are sorted by ascending priorities (so higher numbers to lower numbers).
        for (const known of coreStatus) {
            // We look for the first entry that doesn't have a higher priority.
            if (known.priority >= priority) {
                return priorityToJs(known);
            }
        }
        // If we get to this point, no priority entry exists so we never synced a priority that low.
        return {
            priority,
            lastSyncedAt: undefined,
            hasSynced: false
        };
    }
    isEqual(status) {
        /**
         * By default Error object are serialized to an empty object.
         * This replaces Errors with more useful information before serialization.
         */
        const replacer = (_, value) => {
            if (value instanceof Error) {
                return this.serializeError(value);
            }
            return value;
        };
        const options = { core: this.core, jsState: this.jsState };
        const otherStatus = status;
        const otherOptions = {
            core: otherStatus.core,
            jsState: otherStatus.jsState
        };
        return JSON.stringify(options, replacer) == JSON.stringify(otherOptions, replacer);
    }
    getMessage() {
        return `SyncStatus<connected: ${this.connected} connecting: ${this.connecting} lastSyncedAt: ${this.lastSyncedAt} hasSynced: ${this.hasSynced}. Downloading: ${this.downloading}. Uploading: ${this.uploading}. UploadError: ${this.uploadError}, DownloadError?: ${this.downloadError}>`;
    }
    /**
     * Serializes the SyncStatus instance to a plain object.
     *
     * @returns A plain object representation of the sync status
     */
    toJSON() {
        return {
            core: this.core,
            dataFlow: {
                uploading: this.uploading,
                uploadError: this.serializeError(this.uploadError),
                downloadError: this.serializeError(this.downloadError)
            }
        };
    }
    /**
     * Not all errors are serializable over a MessagePort. E.g. some `DomExceptions` fail to be passed across workers.
     * This explicitly serializes errors in the SyncStatus.
     */
    serializeError(error) {
        if (typeof error == 'undefined') {
            return undefined;
        }
        const serialized = {
            name: error.name,
            message: error.message,
            stack: error.stack
        };
        // `Error.cause` can be any value (the spec types it as unknown). Preserve it
        // so consumers reading uploadError/downloadError keep the failure context.
        // Recurse for Error causes so the whole chain is flattened the same way.
        if (typeof error.cause != 'undefined') {
            serialized.cause = error.cause instanceof Error ? this.serializeError(error.cause) : error.cause;
        }
        return serialized;
    }
}
function coreTimestampToDate(time) {
    return time == null ? undefined : new Date(time / 1000);
}
function priorityToJs(status) {
    return {
        priority: status.priority,
        hasSynced: status.has_synced ?? undefined,
        lastSyncedAt: coreTimestampToDate(status.last_synced_at)
    };
}
class SyncStreamStatusView {
    status;
    core;
    subscription;
    constructor(status, core) {
        this.status = status;
        this.core = core;
        this.subscription = {
            name: core.name,
            parameters: core.parameters,
            active: core.active,
            isDefault: core.is_default,
            hasExplicitSubscription: core.has_explicit_subscription,
            expiresAt: core.expires_at != null ? new Date(core.expires_at * 1000) : null,
            hasSynced: core.last_synced_at != null,
            lastSyncedAt: core.last_synced_at != null ? new Date(core.last_synced_at * 1000) : null
        };
    }
    get progress() {
        if (this.status.core?.downloading == null) {
            // Don't make download progress public if we're not currently downloading.
            return null;
        }
        const { total, downloaded } = this.core.progress;
        const progress = total == 0 ? 0.0 : downloaded / total;
        return { totalOperations: total, downloadedOperations: downloaded, downloadedFraction: progress };
    }
    get priority() {
        return this.core.priority;
    }
}
//# sourceMappingURL=SyncStatus.js.map